import React, { useCallback, useState } from 'react'
import { type CustomIntegration } from '../../../models/CustomIntegration'
import { useUpdateCustomIntegrationMutation } from '../../../store/customIntegration/customIntegration'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { Button } from '../../../components/Button/Button'
import { CustomIntegrationDetailsFormFields } from '../components/CustomIntegrationDetailsFormFields'
import { FormSaveBar } from '../../../components/FormSaveBar/FormSaveBar'
import { buildDetailsPatch, type DetailsFormData, detailsFromCustomIntegration, isDetailsValid, isSameFormValue } from '../form/customIntegrationForm'
import { DeleteCustomIntegrationModal } from './DeleteCustomIntegrationModal'
import { DisconnectUsersWarningModal } from './DisconnectUsersWarningModal'
import { detailsChangeInvalidatesCredentials } from '../form/credentialInvalidation'
import { userApi } from '../../../store/user/user'
import { useUnsavedChangesGuard } from '../../../common/useUnsavedChangesGuard'
import { WizardStepHeader } from '../components/WizardStepCard'

interface CustomIntegrationDetailsTabProps {
  customIntegration: CustomIntegration
  canDelete: boolean
}

export const CustomIntegrationDetailsTab: React.FC<CustomIntegrationDetailsTabProps> = ({ customIntegration, canDelete }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [updateCustomIntegration, { isLoading: isSaving }] = useUpdateCustomIntegrationMutation()
  const [details, setDetails] = useState<DetailsFormData>(() => detailsFromCustomIntegration(customIntegration))
  const [showDelete, setShowDelete] = useState<boolean>(false)
  const [showDisconnectWarning, setShowDisconnectWarning] = useState<boolean>(false)

  const initial = detailsFromCustomIntegration(customIntegration)
  const isDirty = !isSameFormValue(details, initial)
  const disconnectsUsers = detailsChangeInvalidatesCredentials(initial, details)
  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty, isSaving)

  const handleDiscard = useCallback(() => {
    setDetails(detailsFromCustomIntegration(customIntegration))
  }, [customIntegration])

  const save = useCallback(async () => {
    try {
      await updateCustomIntegration({ accountId, customIntegrationId: customIntegration.id, payload: buildDetailsPatch(details) }).unwrap()
      if (disconnectsUsers) {
        dispatch(userApi.util.invalidateTags(['me']))
      }
      dispatch(addToast({ variant: 'success', title: 'Details saved.', messages: disconnectsUsers ? ['Every user needs to connect again.'] : undefined }))
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to save the details. Please try again.'] }))
    } finally {
      setShowDisconnectWarning(false)
    }
  }, [updateCustomIntegration, accountId, customIntegration.id, details, disconnectsUsers, dispatch])

  const handleSave = useCallback(async () => {
    if (disconnectsUsers) {
      setShowDisconnectWarning(true)
      return
    }
    await save()
  }, [disconnectsUsers, save])

  return (
    <div className='d-flex flex-column min-vh-100'>
      <div className='d-flex flex-column flex-grow-1 zocks-gap-2 zocks-pb-10.0'>
        <section className='d-flex flex-column gap-4' aria-label='Manage App'>
          <WizardStepHeader icon='bi-gear' title='Manage App' />
          <div className='px-5 d-flex flex-column gap-4'>
            <CustomIntegrationDetailsFormFields data={details} onChange={setDetails} descriptionRows={3} />
            {canDelete ? (
              <div className='d-flex justify-content-start'>
                <Button variant='danger' className='zocks-px-0.75' onClick={() => setShowDelete(true)}>
                  Delete App
                </Button>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      {canDelete ? <DeleteCustomIntegrationModal show={showDelete} customIntegration={customIntegration} onHide={() => setShowDelete(false)} /> : null}
      <DisconnectUsersWarningModal
        show={showDisconnectWarning}
        name={customIntegration.name}
        isSaving={isSaving}
        onHide={() => setShowDisconnectWarning(false)}
        onConfirm={save}
      />
      <FormSaveBar
        isVisible={isDirty}
        isDirty={isDirty}
        isValid={isDetailsValid(details)}
        isSubmitting={isSaving}
        onDiscard={handleDiscard}
        onSave={handleSave}
        discardLabel='Discard changes'
        saveLabel='Save changes'
        showNavigationWarning={isBlocked}
        onConfirmLeave={confirmLeave}
        onCancelLeave={cancelLeave}
      />
    </div>
  )
}
